import { useMemo } from "react";

import {
    bsMath,
    calculatePercentChange,
    findMaxElement,
    getEtWeekdayHourMinute,
    isTimeBetween,
    isWeekend
} from "@bridgesplit/utils";

import {
    AbfLoanExpanded,
    AbfOrderFundingType,
    Ledger,
    LedgerDebt,
    LedgerExpanded,
    LiquidationType,
    LoanEventActionType,
    MarketTimeRestrictionType,
    ParsedBorrowPrincipalMetadata,
    ParsedLoanEventExpanded,
    ParsedRepayPrincipalMetadata
} from "../types";
import { summarizeLedgerAccount } from "./order";
import { getZcLedger, getZcLoanCollateral, getZcLoanLiquidationThreshold, useLenderStrategies } from "../api";

export function calculateCurrentLtv({
    totalOwedValue,
    loanCollateralValue
}: {
    totalOwedValue: number | undefined;
    loanCollateralValue: number | undefined;
}): number {
    return bsMath.div(totalOwedValue, loanCollateralValue);
}

export function calculateHealthRatio(
    totalOwedValue: number | undefined,
    loanCollateralValue: number | undefined,
    liquidationThreshold: number | undefined
): number {
    if (totalOwedValue === undefined || loanCollateralValue === undefined || liquidationThreshold === undefined)
        return 0;

    if (totalOwedValue === 0) return 1;
    const loanRatio = calculateCurrentLtv({ totalOwedValue, loanCollateralValue });

    return calculateHealthRatioFromLtv(loanRatio, liquidationThreshold);
}

export function calculateHealthRatioFromLtv(ltv: number | undefined, liquidationThreshold: number | undefined): number {
    if (ltv === undefined || liquidationThreshold === undefined) return 0;

    if (ltv === 0) return 1;
    const health = ltv * (1 / liquidationThreshold);
    const healthRatio = Math.max(1 - health, 0);
    return healthRatio;
}

export function calculateLiquidationPrice(
    totalPrincipalAmount: number | undefined,
    loanCollateralAmount: number | undefined,
    liquidationThreshold: number | undefined
): number {
    const collateralizationRatio = bsMath.div(loanCollateralAmount, totalPrincipalAmount);
    const liquidationPrice = bsMath.mul(liquidationThreshold, collateralizationRatio) ?? 0;
    return 1 / liquidationPrice;
}

export function isLoanZeroCoupon(loanExpanded: AbfLoanExpanded | undefined): boolean {
    return loanExpanded?.ledger.length === 1;
}

export function isLoanRefinanced(loanExpanded: AbfLoanExpanded | undefined): boolean {
    if (!loanExpanded) return false;

    // Check loan events for any refinance actions
    return loanExpanded.events.some(
        (event) =>
            isRefinanceEvent(event.action) ||
            (event.originalLender && event.newLender && event.originalLender !== event.newLender)
    );
}

export function isSimpleLoan(loanExpanded: AbfLoanExpanded | undefined): boolean {
    if (!loanExpanded) return false;
    return isLoanZeroCoupon(loanExpanded);
}

export function canLenderSellLoan(loanExpanded: AbfLoanExpanded | undefined): boolean {
    if (!loanExpanded) return false;
    if (isLoanFullyRepaid(loanExpanded)) return false;
    return true;
}

export function isLoopLoan(loanExpanded: AbfLoanExpanded | undefined) {
    return loanExpanded?.loanType === AbfOrderFundingType.Loop;
}

export function isLoanFullyRepaid(loanExpanded: AbfLoanExpanded | undefined) {
    if (!loanExpanded) return false;
    return loanExpanded.ledger.every((l) => l.isFilled);
}

export function getNextPaymentDate(loan: AbfLoanExpanded | undefined): number | undefined {
    if (!loan) {
        return undefined;
    }
    const firstNotFilledElement = loan.ledger.find((el) => !el.isFilled);
    if (!firstNotFilledElement) return undefined;
    return firstNotFilledElement.endTime;
}

export function calculateLoanLiquidationPrice(
    loanExpanded: AbfLoanExpanded | undefined,
    ledgerAccount?: LedgerExpanded
) {
    const firstLedger = getZcLedger(loanExpanded);
    const selectedLedgerAccount = ledgerAccount ?? firstLedger;
    if (!loanExpanded || loanExpanded.collateral.length !== 1 || !selectedLedgerAccount) return undefined;

    const collateral = loanExpanded.collateral[0];
    const totalOutstanding = summarizeLedgerAccount(selectedLedgerAccount).totalOutstanding;
    const collateralInfo = getZcLoanCollateral(loanExpanded);
    const collateralUncertainty = collateralInfo?.uncertainty ?? 0;

    const liquidationThreshold = getZcLoanLiquidationThreshold(loanExpanded);
    const liquidationPrice = calculateLiquidationPrice(totalOutstanding, collateralInfo?.amount, liquidationThreshold);

    const currentPrice = bsMath.div(
        collateralInfo && (collateralInfo.usdPrice ?? 0) + collateralUncertainty,
        loanExpanded.principalUsdPrice
    );
    const percentChange = calculatePercentChange(currentPrice, liquidationPrice);

    return { price: liquidationPrice, collateral, percentChange };
}

export function getLoanTotalDebtForLedger(ledgerAccount: LedgerExpanded | Ledger): LedgerDebt {
    const originalLedgers = summarizeLedgerAccount(ledgerAccount);

    return {
        total: originalLedgers.totalOutstanding,
        interestAccrued: originalLedgers.interestOutstanding
    };
}

export function getNextLedgerAccount(loanExpanded: AbfLoanExpanded | undefined) {
    const nextLedgerAccount = loanExpanded?.ledger.find((l) => !l.isFilled);
    return { nextLedgerAccount };
}

export function isUserParticipant(event: ParsedLoanEventExpanded, role: "seller" | "buyer", walletId: string): boolean {
    if (!walletId) return false;

    if (role === "seller") {
        return walletId === event.originalLender;
    } else {
        return walletId === event.newLender;
    }
}

// Updated event finder functions
export function findMostRecentSellerEvent(loanExpanded: AbfLoanExpanded | undefined, walletId: string) {
    return findMaxElement(
        loanExpanded?.events.filter((e) => isSellLedgerEvent(e.action) && isUserParticipant(e, "seller", walletId)),
        ({ eventTime }) => eventTime
    );
}

export function findMostRecentBuyerEvent(loanExpanded: AbfLoanExpanded | undefined, walletId: string) {
    return findMaxElement(
        loanExpanded?.events.filter((e) => isBorrowEvent(e.action) && isUserParticipant(e, "buyer", walletId)),
        ({ eventTime }) => eventTime
    );
}

export function getTotalLenderSaleProceeds(loanExpanded: AbfLoanExpanded | undefined, wallet: string | undefined) {
    if (!loanExpanded || !wallet) return { proceeds: 0, mostRecentSell: undefined };
    const mostRecentSell = findMostRecentSellerEvent(loanExpanded, wallet);

    const proceeds = mostRecentSell?.amount ?? 0;
    return { proceeds, mostRecentSell };
}

export function calculatePrincipalInterestDeltaFromBorrowOrRepayEvent({
    metaData,
    action
}: {
    metaData: ParsedBorrowPrincipalMetadata | ParsedRepayPrincipalMetadata | undefined;
    action: LoanEventActionType;
}) {
    if (!metaData) return { principalDelta: 0, interestDelta: 0 };
    if (isBorrowEvent(action)) {
        const { preBorrowPrincipalLedgerState: pre, postBorrowPrincipalLedgerState: post } =
            metaData as ParsedBorrowPrincipalMetadata;
        return {
            principalDelta: Math.abs(post.principalDue - pre.principalDue),
            interestDelta: Math.abs(post.interestDue - pre.interestDue)
        };
    } else {
        const { preRepaymentPrincipalLedgerState: pre, postRepaymentPrincipalLedgerState: post } =
            metaData as ParsedRepayPrincipalMetadata;
        return {
            principalDelta: Math.abs(post.principalRepaid - pre.principalRepaid),
            interestDelta: Math.abs(post.interestDue - pre.interestDue)
        };
    }
}

export function isBorrowEvent(action: LoanEventActionType | undefined) {
    return action === LoanEventActionType.BorrowPrincipal;
}

export function isRepayEvent(action: LoanEventActionType | undefined) {
    return action === LoanEventActionType.RepayPrincipal;
}

export function isLiquidateEvent(action: LoanEventActionType | undefined) {
    return action === LoanEventActionType.Liquidate;
}

export function isFullLiquidationEvent(event: ParsedLoanEventExpanded) {
    const { liquidationType } = event.actionMetadata[LoanEventActionType.Liquidate];
    return liquidationType === LiquidationType.FullLiquidation;
}

export function isRefinanceEvent(action: LoanEventActionType | undefined) {
    return action === LoanEventActionType.RefinanceLedger;
}

export function isSellLedgerEvent(action: LoanEventActionType | undefined) {
    return action === LoanEventActionType.SellLedger;
}

export function useIsLoanLender(loanExpanded: AbfLoanExpanded | undefined, activeWallet: string | undefined) {
    const { data: strategies } = useLenderStrategies();

    const strategyAddress = useMemo(() => strategies?.map((s) => s.strategy.address), [strategies]);

    if (!loanExpanded || !activeWallet || !strategyAddress) return false;
    const isLender = loanExpanded.lenders.some((lender) => strategyAddress.includes(lender));
    return isLender;
}

/**
 * Returns true if the current time is within the market hours for US Equities and FX/Metals
 * Hardcoded hours while we don't have a real API
 */
export function getRestrictedMarketsStatus(): Record<MarketTimeRestrictionType, boolean> {
    const [dayOfWeek, hour, minute] = getEtWeekdayHourMinute();
    const isWeekendDay = isWeekend(dayOfWeek);

    // US Equities - Monday to Friday, 9:30 AM to 4:00 PM ET
    const isUSEquitiesOpen =
        !isWeekendDay && isTimeBetween({ hour, minute }, { hour: 9, minute: 30 }, { hour: 16, minute: 0 });

    // FX and Metals - Sunday 5:00 PM ET to Friday 5:00 PM ET with break every day from 5-6PM
    const isFXMetalsOpen =
        !(dayOfWeek === "Saturday") &&
        !(dayOfWeek === "Sunday" && hour <= 18) &&
        !isTimeBetween({ hour, minute }, { hour: 17, minute: 0 }, { hour: 18, minute: 0 });

    const marketHours = {
        [MarketTimeRestrictionType.UsEquities]: isUSEquitiesOpen,
        [MarketTimeRestrictionType.Fx]: isFXMetalsOpen,
        [MarketTimeRestrictionType.Metals]: isFXMetalsOpen
    };
    return marketHours;
}
